import styled from 'styled-components';

export const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.surface};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

export const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const WindowNote = styled(Muted)`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

export const Caption = styled.h4`
  margin: ${({ theme }) => theme.spacing(5)} 0 ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const ExpandButton = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  svg {
    flex: none;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;

export const DetailCell = styled.td`
  background: ${({ theme }) => theme.colors.surface};
`;

export const OfferList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

/** Строка предложения: радио + поставщик + цена + дата. `$dim` — предложение
 *  вне сравнения (чужая единица/валюта, неразобранная цена): показываем, но
 *  приглушаем, чтобы его не приняли за участника минимума. */
export const OfferItem = styled.label<{ $dim: boolean; $selectable: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(120px, 1.4fr) minmax(90px, 0.8fr) auto auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  opacity: ${({ $dim }) => ($dim ? 0.55 : 1)};
  cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'default')};

  &:hover {
    background: ${({ $selectable, theme }) =>
      $selectable ? theme.colors.background : 'transparent'};
  }

  @media (max-width: 640px) {
    grid-template-columns: auto 1fr;
    row-gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const OfferSupplier = styled.span`
  font-weight: 600;
`;

/** Цена — текст из КП как есть, бывает длинным («6 382 900 СМУ (Юань КНР) по
 *  курсу ЦБ на день оплаты»): переносим внутри своей колонки, иначе он
 *  наезжал на значок «минимум» и дату КП. */
export const OfferPrice = styled.span`
  min-width: 0;
  text-align: right;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

export const OfferMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;

export const SelectionBar = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const TextInput = styled.input`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`;

/** Основание выбора — тянется в остатке строки рядом с кнопкой. */
export const ReasonInput = styled(TextInput)`
  flex: 1 1 240px;
  min-width: 0;
`;

/** Поиск по справочнику стоит в ячейке таблицы, а не во flex-строке: без
 *  явной ширины input ужимается до дефолтных ~20 символов, и длинное
 *  наименование позиции в нём не прочитать. */
export const SearchInput = styled(TextInput)`
  width: 100%;
`;

export const CurrentSelection = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  max-height: 200px;
  overflow-y: auto;
`;

export const SearchHit = styled.button`
  display: block;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`;

export const HitSub = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** Форма «Добавить в справочник»: наименование тянется, единица — узкая. */
export const NewItemForm = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const NameInput = styled(TextInput)`
  flex: 1 1 280px;
  min-width: 0;
`;

export const UnitInput = styled(TextInput)`
  width: 96px;
`;

export const CaptionAction = styled.span`
  margin-left: auto;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
`;

export const Notice = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

/** Цена в списке несопоставленных — текст из КП как есть, бывает длинным
 *  («1 480 000 Юань (оплата в рублях по курсу ЦБ…)»): переносится внутри
 *  графы, иначе таблица уезжала вбок и цены обрезались. */
export const PriceCell = styled.td`
  text-align: right;
  min-width: 110px;
  max-width: 220px;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
`;
